import { useEffect, useMemo, useRef } from "react";
import { Category, ContextPage, EventType } from "../constants";
import { enqueueImpression } from "../impressionBatcher";
import { useTrackingRuntime } from "../TrackingProvider";
import { v7 as uuidv7 } from "uuid";
import { safeReadSessionJson, safeWriteSessionJson } from "../storage";

const seenKeyForPage = (page) => `tracking.impressions.seen.v1.${page || "UNKNOWN"}`;
const nowIso = () => new Date().toISOString();

const readSeen = (page) => {
  const value = safeReadSessionJson(seenKeyForPage(page), {});
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
};

const writeSeen = (page, value) => {
  safeWriteSessionJson(seenKeyForPage(page), value);
};

export const useImpressionTracking = ({
  publicationId,
  establishmentId,
  page = ContextPage.TRIPS_LIST,
}) => {
  const runtime = useTrackingRuntime();
  const ref = useRef(null);
  const timerRef = useRef(null);
  const firedRef = useRef(false);

  const canTrack = Boolean(publicationId) && Boolean(establishmentId);

  const observerOptions = useMemo(() => ({ threshold: [0, 0.5, 1] }), []);

  useEffect(() => {
    if (!canTrack) return undefined;

    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;

    const seen = readSeen(page);
    if (seen[String(publicationId)]) {
      firedRef.current = true;
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const visibleEnough = entry.isIntersecting && entry.intersectionRatio >= 0.5;

      if (!visibleEnough) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        return;
      }

      if (firedRef.current || timerRef.current) return;

      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        if (firedRef.current) return;

        const seenLatest = readSeen(page);
        if (seenLatest[String(publicationId)]) {
          firedRef.current = true;
          return;
        }

        firedRef.current = true;
        seenLatest[String(publicationId)] = Date.now();
        writeSeen(page, seenLatest);

        enqueueImpression({
          schemaVersion: runtime.schemaVersion,
          eventId: uuidv7(),
          eventTime: nowIso(),
          category: Category.VISIBILITY_EVENT,
          eventType: EventType.SEARCH_IMPRESSION,
          establishmentId: String(establishmentId),
          publicationId: String(publicationId),
          visitorCountry: runtime.visitorCountry?.countryCode || undefined,
          context: { source: runtime.trackingSource, page },
        });
      }, 1000);
    }, observerOptions);

    observer.observe(el);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      observer.disconnect();
    };
  }, [canTrack, establishmentId, page, observerOptions, publicationId, runtime]);

  return { ref };
};
